import {
  ArrowRight,
  Check,
  ChevronDown,
  FileCheck2,
  GitBranch,
  MessageSquareText,
  ReceiptText,
  Scale,
  Sparkles,
} from "lucide-react";
import { track } from "@/lib/analytics";
import { ga4SignUpStart } from "@/lib/ga4";
import { WhatBecomesPossible } from "@/marketing/PossibilityLayer";

const problems = [
  {
    icon: MessageSquareText,
    title: "The quote that only exists in a chat",
    body: "Someone got a better price from a second supplier. It was on their phone. They left the company. Nobody can find it, and the next purchase starts from zero.",
  },
  {
    icon: FileCheck2,
    title: "The order nobody remembers approving",
    body: "The invoice arrives and finance asks who signed off. The answer is a verbal yes in a corridor six weeks ago, and there is nothing to show an auditor.",
  },
  {
    icon: ReceiptText,
    title: "The invoice that quietly did not match",
    body: "You ordered 40, received 32, paid for 40. Nobody was careless. There was just never a moment where those three numbers sat on the same screen.",
  },
] as const;

const phases = [
  {
    number: "1",
    title: "Ask and approve",
    body: "Capture what is needed, then route the request through the right approval path.",
    steps: ["Request", "Approve"],
  },
  {
    number: "2",
    title: "Source and award",
    body: "Invite comparable quotations and keep the commercial decision attached to the evidence.",
    steps: ["Source", "Compare"],
  },
  {
    number: "3",
    title: "Buy and close",
    body: "Issue the order, record what arrived and reconcile the documents that close the purchase.",
    steps: ["Buy", "Receive", "Reconcile"],
  },
] as const;

const capabilityGroups = [
  {
    title: "Core flow",
    items: [
      ["Requisition → RFQ traceability", "AVAILABLE"],
      ["Supplier-acknowledged purchase orders", "AVAILABLE"],
      ["Partial and full goods receipt", "BETA"],
      ["Aggregate PO, full-receipt and invoice match", "BETA"],
    ],
  },
  {
    title: "Control and evidence",
    items: [
      ["Payment and reconciliation evidence", "AVAILABLE"],
      ["Country-aware procurement reports and source-linked audit evidence", "AVAILABLE"],
      ["An audit trail nobody can quietly edit", "AVAILABLE"],
    ],
  },
  {
    title: "In development",
    items: [
      ["Obligation Ledger", "BETA", "Track what a supplier still owes after the order: milestones, renewals, warranties and service commitments."],
      ["Avoidable Spend Firewall", "BETA", "Surface existing stock, contracts, coverage and duplicate subscriptions before another purchase is approved."],
      ["Warranty and AMC recovery", "BETA", "Keep coverage dates, service visits and recovery evidence connected to the assets they protect."],
      ["Asset-linked consumables", "BETA", "Approve device compatibility, record reagent or consumable usage by lot and feed governed replenishment."],
    ],
  },
] as const;

const questions = [
  {
    question: "We already use Excel and Tally. Why change?",
    answer: "Excel holds the numbers but not the decision. RAQOZ keeps the request, approval, quotations and delivery record attached to each other, so a purchase can be explained months later. Spreadsheet import and register export are available in the supported workflows.",
  },
  {
    question: "Will my suppliers actually use it?",
    answer: "Suppliers are invited by email and use a secure supplier workspace at no cost. A supplier account is required so quotations and order acknowledgements stay attributable.",
  },
  {
    question: "How long does setup take?",
    answer: "Start with the guided setup, spreadsheet imports for suppliers and items, and approval templates. Timing depends on how many locations, roles and policies you configure.",
  },
  {
    question: "Does RAQOZ move money?",
    answer: "No. RAQOZ records payment status and evidence so invoices, receipts and payments reconcile. Funds move through your bank as they do today.",
  },
  {
    question: "What happens to my data if we leave?",
    answer: "You can generate a governed tenant-wide export in JSON, zipped CSV, Excel-compatible workbook and print/PDF form, alongside record-specific evidence packs. Attachment metadata is included; protected file binaries remain under their original access controls.",
  },
] as const;

export function HomePage() {
  return (
    <>
      <section className="overflow-hidden bg-surface-ink px-5 py-20 text-on-ink md:px-8 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1.22fr_1fr] lg:items-center">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-line-dark bg-teal-wash px-4 py-2 text-xs font-bold tracking-[.13em] text-teal-bright">
              <Sparkles aria-hidden="true" className="size-3.5" />
              PROCUREMENT, ORCHESTRATED
            </p>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[.98] tracking-[-.045em] text-on-ink-strong sm:text-6xl lg:text-[5.2rem]">
              Stop buying through <span className="text-teal-bright">WhatsApp, Excel and email.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-xl leading-8 text-on-ink">
              Procurement software that keeps every request, approval, quotation, order, receipt and invoice connected.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-7 text-on-ink-muted">Turn every purchase into progress. Pain gets attention. Possibility creates desire. Evidence earns belief.</p>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <a
                className="marketing-button-primary-dark"
                href="/signup"
                onClick={() => { track("cta_click", { cta: "start_free", source: "hero" }); ga4SignUpStart("hero_start_free"); }}
              >
                Start free
              </a>
              <a className="marketing-text-link-dark" href="/product">
                See the product <ArrowRight aria-hidden="true" className="size-4" />
              </a>
            </div>
            <p className="mt-5 text-sm text-on-ink-muted">Free plan, no card. Guided setup and spreadsheet imports included.</p>
          </div>
          <div className="lg:-mr-20 xl:-mr-32">
            <QuoteComparisonPlaceholder />
          </div>
        </div>
      </section>

      <WhatBecomesPossible />

      <section className="bg-surface-paper px-5 py-20 text-on-paper md:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <p className="marketing-eyebrow-light">THE PROBLEM</p>
          <h2 className="mt-4 max-w-4xl font-serif text-4xl tracking-[-.035em] sm:text-5xl">
            Three things that go wrong every month.
          </h2>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {problems.map(({ icon: Icon, title, body }) => (
              <article className="rounded-2xl border border-line-light bg-surface-card p-7" key={title}>
                <span className="grid size-10 place-items-center rounded-xl bg-teal-wash text-teal-deep">
                  <Icon aria-hidden="true" className="size-5" />
                </span>
                <h3 className="mt-6 font-serif text-2xl leading-tight">{title}</h3>
                <p className="mt-4 text-sm leading-7 text-on-paper-muted">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line-light bg-surface-paper px-5 py-20 text-on-paper md:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <p className="marketing-eyebrow-light">USEFUL BEFORE SIGNUP</p>
          <div className="mt-4 grid gap-8 lg:grid-cols-[1fr_.72fr] lg:items-end">
            <div>
              <h2 className="max-w-4xl font-serif text-4xl tracking-[-.035em] sm:text-5xl">Do today&apos;s procurement task for free.</h2>
              <p className="mt-5 max-w-3xl text-lg leading-8 text-on-paper-muted">Compare supplier quotations, build an approval matrix, check order-receipt-invoice differences or download an editable template. No account required.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end"><a className="marketing-button-primary-light" href="/tools">Use free tools</a><a className="marketing-button-secondary-light" href="/templates">Download templates</a></div>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[["Quotation comparison", "/tools/vendor-quotation-comparison"], ["Approval matrix", "/tools/procurement-approval-matrix-builder"], ["Three-way match", "/tools/three-way-match-checker"]].map(([label, href]) => <a className="rounded-2xl border border-line-light bg-surface-card p-5 font-semibold hover:border-teal-deep" href={href} key={href}>{label}<ArrowRight aria-hidden="true" className="mt-4 size-4 text-teal-deep" /></a>)}
          </div>
        </div>
      </section>

      <section className="bg-surface-ink px-5 py-20 text-on-ink md:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <p className="marketing-eyebrow-dark">HOW IT WORKS</p>
          <h2 className="mt-4 max-w-4xl font-serif text-4xl tracking-[-.035em] text-on-ink-strong sm:text-5xl">
            One purchase. Three accountable phases.
          </h2>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {phases.map((phase) => (
              <article className="rounded-2xl border border-line-dark bg-surface-ink-raised p-6" key={phase.title}>
                <p className="font-mono text-xs tracking-[.14em] text-teal-bright">{phase.number}</p>
                <h3 className="mt-4 font-serif text-3xl text-on-ink-strong">{phase.title}</h3>
                <p className="mt-3 min-h-20 text-sm leading-6 text-on-ink">{phase.body}</p>
                <div className="mt-5 flex flex-wrap gap-2" aria-label={`${phase.title} steps`}>
                  {phase.steps.map((step) => (
                    <span className="rounded-md border border-line-dark bg-teal-wash px-2.5 py-1 text-[11px] font-semibold text-teal-bright" key={step}>
                      {step}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-surface-paper px-5 py-20 text-on-paper md:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <Scale aria-hidden="true" className="size-8 text-teal-deep" />
          <h2 className="mt-6 max-w-6xl font-serif text-5xl leading-[1.02] tracking-[-.045em] sm:text-6xl lg:text-7xl">
            Everyone can use it. You don&apos;t pay per person.
          </h2>
          <p className="mt-7 max-w-4xl text-lg leading-8 text-on-paper-muted">
            Procurement software usually charges for every seat, so companies limit it to the procurement team and everyone else goes back to email. RAQOZ prices the procurement and admin users. Ordinary requesters and approvers are not counted against you.
          </p>
          <a className="marketing-text-link-light mt-8" href="/pricing">
            See pricing <ArrowRight aria-hidden="true" className="size-4" />
          </a>
        </div>
      </section>

      <section className="border-t border-line-light bg-surface-paper px-5 py-20 text-on-paper md:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <p className="marketing-eyebrow-light">WHAT&apos;S BUILT</p>
          <h2 className="mt-4 max-w-4xl font-serif text-4xl tracking-[-.035em] sm:text-5xl">
            The operating record behind every purchase.
          </h2>
          <div className="mt-12 grid gap-8 lg:grid-cols-3">
            {capabilityGroups.map((group) => (
              <section aria-labelledby={`capability-${group.title.replaceAll(" ", "-").toLowerCase()}`} key={group.title}>
                <h3 className="border-b border-line-light pb-4 text-xs font-bold tracking-[.13em] text-teal-deep uppercase" id={`capability-${group.title.replaceAll(" ", "-").toLowerCase()}`}>
                  {group.title}
                </h3>
                <div className="divide-y divide-line-light">
                  {group.items.map(([title, status, description]) => (
                    <article className="py-5" key={title}>
                      <div className="flex items-start gap-3">
                        {status === "AVAILABLE" ? (
                          <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-teal-deep" />
                        ) : (
                          <GitBranch aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-teal-deep" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold leading-5">{title}</p>
                          {description ? <p className="mt-2 text-sm leading-6 text-on-paper-muted">{description}</p> : null}
                        </div>
                        <HomepageStatus value={status} />
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-surface-ink px-5 py-20 text-on-ink md:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <p className="marketing-eyebrow-dark">SUPPLIERS</p>
          <h2 className="mt-4 max-w-4xl font-serif text-4xl tracking-[-.035em] text-on-ink-strong sm:text-5xl">
            Your suppliers can answer without paying anything.
          </h2>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-on-ink">
            Suppliers use a secure workspace to respond to invitations, submit quotes and acknowledge orders at no cost. Recorded delivery evidence can inform buyer-side trust context; public reliability ranking remains planned, and paid placement cannot buy it.
          </p>
          <a className="marketing-text-link-dark mt-8" href="/supplier-network">
            How the supplier network works <ArrowRight aria-hidden="true" className="size-4" />
          </a>
        </div>
      </section>

      <section className="bg-surface-paper px-5 py-20 text-on-paper md:px-8 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <p className="marketing-eyebrow-light">PRICING</p>
            <h2 className="mt-4 max-w-4xl font-serif text-4xl tracking-[-.035em] sm:text-5xl">
              Free to start. $49/month when you&apos;re ready.
            </h2>
            <p className="mt-5 text-lg leading-8 text-on-paper-muted">Published prices, no sales call, no demo gate. India pricing in rupees.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a className="marketing-button-primary-light" href="/signup" onClick={() => { track("cta_click", { cta: "start_free", source: "pricing_teaser" }); ga4SignUpStart("pricing_teaser_start_free"); }}>
              Start free
            </a>
            <a className="marketing-button-secondary-light" href="/pricing">See all plans</a>
          </div>
        </div>
      </section>

      <section className="bg-surface-ink px-5 py-20 text-on-ink md:px-8 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.72fr_1.28fr]">
          <div>
            <p className="marketing-eyebrow-dark">QUESTIONS</p>
            <h2 className="mt-4 font-serif text-4xl tracking-[-.035em] text-on-ink-strong sm:text-5xl">Clear answers before you start.</h2>
          </div>
          <div className="divide-y divide-line-dark border-y border-line-dark">
            {questions.map(({ question, answer }) => (
              <details className="group" key={question}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-5 py-6 font-semibold text-on-ink-strong marker:content-none">
                  {question}
                  <ChevronDown aria-hidden="true" className="size-5 shrink-0 text-teal-bright transition-transform group-open:rotate-180" />
                </summary>
                <p className="max-w-3xl pb-6 pr-10 text-sm leading-7 text-on-ink">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line-dark bg-surface-ink px-5 py-20 text-on-ink md:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl rounded-2xl border border-line-dark bg-surface-ink-raised p-8 md:p-12">
          <h2 className="mt-6 font-serif text-4xl tracking-[-.035em] text-on-ink-strong sm:text-5xl">Start with one department.</h2>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-on-ink">Free plan, five users, no card. Bring one category of spend into RAQOZ and see whether the record holds up.</p>
          <a className="marketing-button-primary-dark mt-8" href="/signup" onClick={() => { track("cta_click", { cta: "start_free", source: "closing" }); ga4SignUpStart("closing_start_free"); }}>
            Start free
          </a>
        </div>
      </section>
    </>
  );
}

function QuoteComparisonPlaceholder() {
  return (
    <figure className="marketing-product-frame overflow-hidden rounded-2xl border border-line-dark bg-surface-card text-on-paper">
      <img alt="RAQOZ quotation comparison interface with synthetic supplier data" className="aspect-[16/9] w-full object-cover object-top" decoding="async" fetchPriority="high" height="720" src="/screenshots/raqoz-quotation-comparison-interface.png" width="1280" />
      <figcaption className="sr-only">RAQOZ quotation-comparison interface rendered with fictional, synthetic data.</figcaption>
    </figure>
  );
}

function HomepageStatus({ value }: { value: "AVAILABLE" | "BETA" }) {
  const style = value === "AVAILABLE"
    ? "bg-status-available-bg text-teal-deep"
    : "bg-status-beta-bg text-status-beta-on-light";
  return <span className={`shrink-0 rounded-md px-2 py-1 text-[10px] font-semibold tracking-[.1em] ${style}`}>{value}</span>;
}
